import { useMutation, useQuery } from "convex/react";
import {
  Bell,
  Mail,
  Copy,
  CreditCard,
  AlertTriangle,
  FileText,
  HelpCircle,
  LogOut,
  Shield,
  Trash2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../../../convex/_generated/api";
import { APP_MAILTO } from "../../../lib/appLinks";
import { useAuth } from "../hooks/useAuth";
import { EmptyState } from "../components/ui/EmptyState";
import { setLocale, getLocale, type SupportedLocale } from "../i18n";
import { t } from "../i18n";
import { useState } from "react";
import {
  subscribeToPush,
  unsubscribeFromPush,
  getExistingSubscription,
  serializeSubscription,
} from "../lib/webPushRegistration";

const languageOptions: { value: SupportedLocale; labelKey: string }[] = [
  { value: "en", labelKey: "accountLanguageEn" },
  { value: "pt", labelKey: "accountLanguagePt" },
  { value: "es", labelKey: "accountLanguageEs" },
];

export function AppAccountPage() {
  const { session, signOut } = useAuth();
  const navigate = useNavigate();
  const [currentLocale, setCurrentLocale] = useState<SupportedLocale>(getLocale());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSavingNotifications, setIsSavingNotifications] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const user = useQuery(api.users.current, session ? {} : "skip");
  const subscription = useQuery(api.monitors.getUserSubscriptionStatus, session ? {} : "skip");
  const updateNotifications = useMutation(api.users.updateNotificationSettings);
  const registerPushToken = useMutation(api.pushTokens.registerPushToken);
  const unregisterPushToken = useMutation(api.pushTokens.unregisterPushToken);
  const deleteUser = useMutation(api.users.deleteUser);

  const hasPremium = subscription?.isPremium ?? false;
  const planLabel = hasPremium
    ? (subscription?.promoCode ? `Promo ${subscription.promoCode}` : "Premium")
    : t("billingFree");
  const planLimit = subscription?.planLimit ?? 0;
  const statusLabel = hasPremium
    ? t("statusActive")
    : (subscription?.hasSubscriptionHistory ? t("billingNoSubscription") : t("billingFree"));
  const expiryLabel = subscription?.expiresAt
    ? new Date(subscription.expiresAt).toLocaleDateString()
    : null;

  const handleLanguageChange = (locale: SupportedLocale) => {
    setLocale(locale);
    setCurrentLocale(locale);
  };

  const handleToggleNotifications = async () => {
    if (!user) return;
    setErrorMessage(null);
    setStatusMessage(null);
    setIsSavingNotifications(true);

    const willEnable = !user.notificationsEnabled;

    try {
      if (willEnable) {
        if (!("Notification" in window) || !("serviceWorker" in navigator) || !("PushManager" in window)) {
          setErrorMessage(t("accountNotificationsNotSupported"));
          return;
        }

        if (Notification.permission === "denied") {
          setErrorMessage(t("accountNotificationsBlocked"));
          return;
        }

        const permission = await Notification.requestPermission();
        if (permission !== "granted") {
          setErrorMessage(t("accountNotificationsBlocked"));
          return;
        }

        const subscription = await subscribeToPush();
        if (!subscription) {
          setErrorMessage(t("accountNotificationsError"));
          return;
        }

        const tokenString = serializeSubscription(subscription);
        await registerPushToken({ token: tokenString, platform: "web" });
      } else {
        const existingSubscription = await getExistingSubscription();
        if (existingSubscription) {
          await unregisterPushToken({ token: serializeSubscription(existingSubscription) });
        }
        await unsubscribeFromPush();
      }

      await updateNotifications({ notificationsEnabled: willEnable });
      setStatusMessage(t("accountNotificationsSaved"));
    } catch (error) {
      console.error("Failed to toggle notifications:", error);
      setErrorMessage(t("accountNotificationsError"));
    } finally {
      setIsSavingNotifications(false);
    }
  };

  const handleToggleEmailNotifications = async () => {
    if (!user) return;
    setErrorMessage(null);
    setStatusMessage(null);
    try {
      await updateNotifications({
        notificationsEnabled: user.notificationsEnabled,
        emailNotificationsEnabled: !user.emailNotificationsEnabled,
      });
      setStatusMessage(t("accountNotificationsSaved"));
    } catch {
      setErrorMessage(t("accountNotificationsError"));
    }
  };

  const handleCopyUserId = async () => {
    if (!session) return;
    setErrorMessage(null);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(session.userId);
      }
      setStatusMessage(t("settingsCopied"));
    } catch {
      setErrorMessage(t("accountCopyError"));
    }
  };

  const handleDeleteAccount = async () => {
    setErrorMessage(null);
    setIsDeleting(true);
    try {
      await deleteUser({});
      await signOut();
    } catch {
      setErrorMessage(t("accountDeleteError"));
      setIsDeleting(false);
    }
  };

  if (!session) {
    return (
      <EmptyState
        title={t("accountSignInTitle")}
        body={t("accountSignInBody")}
      />
    );
  }

  return (
    <div className="app-page app-account-page">
      <div className="app-page-header">
        <div>
          <p className="app-eyebrow">{t("appAccount")}</p>
          <h1>{t("settingsTitle")}</h1>
        </div>
      </div>

      {statusMessage && <p className="app-settings-note">{statusMessage}</p>}
      {errorMessage && <p className="app-settings-error">{errorMessage}</p>}

      <section className="app-settings-card app-account-subscription-card">
        <h2>{t("billingTitle")}</h2>
        <button
          type="button"
          className="app-settings-row"
          onClick={() => navigate(hasPremium ? "/app/billing" : "/app/renew")}
          disabled={subscription === undefined}
        >
          <span className="app-settings-icon"><CreditCard size={18} /></span>
          <span>
            <strong>{planLabel}</strong>
            <small>
              {expiryLabel
                ? t("billingExpiresAt", { date: expiryLabel })
                : t("accountSubscriptionStatus", { status: statusLabel })}
            </small>
          </span>
          <span className={`app-status-pill ${hasPremium ? "app-status-success" : "app-status-muted"}`}>
            <span />
            {statusLabel}
          </span>
        </button>
        <p className="app-settings-note">
          {t("accountSubscriptionLimit", { count: planLimit })}
        </p>
        <button
          type="button"
          className="app-primary-button app-settings-full-action"
          onClick={() => navigate(hasPremium ? "/app/billing" : "/app/renew")}
          disabled={subscription === undefined}
        >
          {hasPremium ? t("billingManage") : t("renewalCta")}
        </button>
        {user?.email && (
          <button
            className="app-settings-row"
            onClick={() => void handleToggleEmailNotifications()}
            disabled={user === undefined}
          >
            <span className="app-settings-icon"><Mail size={18} /></span>
            <span>
              <strong>{t("settingsEmailNotifications")}</strong>
              <small>{t("settingsEmailNotificationsDesc")}</small>
            </span>
            <span className={`app-toggle ${user.emailNotificationsEnabled ? "is-on" : ""}`} />
          </button>
        )}
      </section>

      <section className="app-settings-card">
        <h2>{t("settingsNotifications")}</h2>
        <button
          className="app-settings-row"
          onClick={handleToggleNotifications}
          disabled={isSavingNotifications || user === undefined}
        >
          <span className="app-settings-icon"><Bell size={18} /></span>
          <span>
            <strong>{t("settingsNotifications")}</strong>
            <small>{t("settingsNotificationsDesc")}</small>
          </span>
          <span className={`app-toggle ${user?.notificationsEnabled ? "is-on" : ""}`} />
        </button>
      </section>

      <section className="app-settings-card">
        <h2>{t("accountLanguage")}</h2>
        <div className="app-language-row">
            {languageOptions.map(({ value, labelKey }) => (
              <button
                key={value}
                onClick={() => handleLanguageChange(value)}
                className={currentLocale === value ? "is-active" : ""}
              >
                {t(labelKey)}
              </button>
            ))}
        </div>
      </section>

      <section className="app-settings-card">
        <h2>{t("settingsSupport")}</h2>
        <a className="app-settings-row" href={APP_MAILTO.support}>
          <span className="app-settings-icon"><HelpCircle size={18} /></span>
          <strong>{t("settingsHelp")}</strong>
        </a>
        <a className="app-settings-row" href={APP_MAILTO.reportProblem}>
          <span className="app-settings-icon"><AlertTriangle size={18} /></span>
          <strong>{t("settingsReport")}</strong>
        </a>
      </section>

      <section className="app-settings-card">
        <h2>{t("settingsAbout")}</h2>
        <button className="app-settings-row" onClick={handleCopyUserId}>
          <span className="app-settings-icon"><Copy size={18} /></span>
          <span>
            <strong>{t("settingsUserId")}</strong>
            <small>{session.userId}</small>
          </span>
        </button>
        <div className="app-settings-row">
          <span className="app-settings-icon"><FileText size={18} /></span>
          <span>
            <strong>{t("settingsVersion")}</strong>
            <small>0.1.0 web</small>
          </span>
        </div>
      </section>

      <section className="app-settings-card">
        <h2>{t("settingsLegal")}</h2>
        <a className="app-settings-row" href="/terms">
          <span className="app-settings-icon"><FileText size={18} /></span>
          <strong>{t("settingsTerms")}</strong>
        </a>
        <a className="app-settings-row" href="/privacy">
          <span className="app-settings-icon"><Shield size={18} /></span>
          <strong>{t("settingsPrivacy")}</strong>
        </a>
      </section>

      <section className="app-settings-card">
        <button className="app-settings-row" onClick={signOut}>
          <span className="app-settings-icon"><LogOut size={18} /></span>
          <strong>{t("appSignOut")}</strong>
        </button>
          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="app-settings-row is-danger"
            >
              <span className="app-settings-icon"><Trash2 size={18} /></span>
              <strong>{t("settingsDeleteAccount")}</strong>
            </button>
          ) : (
            <div className="app-danger-confirm">
              <p>{t("accountDeleteConfirmText")}</p>
              {subscription?.isPremium && <p>{t("accountDeleteSubscriptionWarning")}</p>}
              <div>
                {subscription?.isPremium && (
                  <button
                    type="button"
                    onClick={() => navigate("/app/billing")}
                    className="app-secondary-button"
                    disabled={isDeleting}
                  >
                    {t("billingManage")}
                  </button>
                )}
                <button onClick={handleDeleteAccount} className="app-danger-button" disabled={isDeleting}>
                  {isDeleting ? t("commonLoading") : t("accountDeleteConfirmBtn")}
                </button>
                <button onClick={() => setShowDeleteConfirm(false)} className="app-secondary-button" disabled={isDeleting}>
                  {t("accountCancel")}
                </button>
              </div>
            </div>
          )}
      </section>
    </div>
  );
}
